// client-rate-cards.ts — the dedicated client grid.
//
// One object per entry replaces the three legacy mechanisms (specific discount, dedicated tiers,
// net price). An entry carries a target, a base (% or €), an optional tier scale, optional client
// minimums, a public-rate flag and an active flag. Only one active entry per target.
//
// Three governing rules: anything the grid does not specify falls back to the normal PV; an entry
// substitutes the applicable client discount rather than stacking on it; and PA is never moved.
//
// Composition at tier t: r(t) = min(0.60, 1 − (1 − base) × (1 − scale(t))). A tier priced in €
// fixes the client price as-is, and its displayed % is the inverse calculation against the target's
// full catalogue price (it may be negative, which is a markup rather than an error).
//
// `migrateLegacyRateCards` converts the legacy form on the fly, cent-for-cent identical.

import { num, round2 } from "../core/core";
import { engineMessage } from "../core/messages";
import { marginTier, type VolumeGrid } from "../pricing/volume-tiers";

// ————————————————————————————————————————————————————————————————
// The unified model
// ————————————————————————————————————————————————————————————————

/** A scale tier: either a rate (composed with the base) or a € price (as-is).
 *  Neither one means the tier falls back to the standard rate at its lower bound. */
export interface ScaleTier {
  minThreshold: number;
  /** Tier rate (fraction 0..1), composed with the base, capped at 60%. */
  rate?: number;
  /** Client price of the tier (€). Mutually exclusive with `rate`: a price is not recomposed. */
  price?: number;
}

/** Target of an entry: a saved combination, a catalogue item (material or component), or a service. */
export type EntryTarget = "combination" | "item" | "service";

/** One entry of the dedicated client grid. */
export interface RateCardEntry {
  id: string;
  targetType: EntryTarget;
  /** Targeted refs — several refs may share one rule. */
  refs: readonly string[];
  /** Label (badge / tooltip). */
  name?: string;
  /** Base as a % (fraction 0..1). An explicit 0 counts as set and substitutes the agency discount
   *  with zero. Absent, together with an absent `basePrix`, means the base is inherited from the
   *  quote's agency discount. */
  base?: number;
  /** Base in €: the other way to write the same setting. Mutually exclusive with `base`. */
  basePrice?: number;
  /** Tier scale. Absent means the default scale (grid or standard thresholds, standard rate at each
   *  lower bound). When present it is self-contained: its `minThreshold` values are the entry's thresholds. */
  scale?: readonly ScaleTier[];
  /** Client minimum unit price (€/piece), substituting the target's catalogue value in the element floor. */
  clientMinUnitPrice?: number;
  /** Client minimum flat fee (€), substituting the material fee in the consolidation. */
  clientFlatFee?: number;
  /** Documentary public-rate flag: marks an entry that shows the target at the public rate (base 0,
   *  default scale). Pricing never reads it; the base and scale alone decide the price. */
  showcase?: boolean;
  /** Active flag (never dates). Absent means active. */
  active?: boolean;
  /** Migration compatibility only: a legacy net price on an item overrode the whole line's PV.
   *  This flag preserves that scope so existing quotes still total the same. */
  linePriceScope?: boolean;
}

/** A client's grid: its threshold set (absent means the general grid's) and its entries. */
export interface DedicatedClientRateCard {
  /** Threshold set (m², or linear metres for a per-metre accessory), ascending, first tier at 0.
   *  Entries without their own scale inherit it. Absent means the standard thresholds. */
  thresholds?: readonly number[];
  entries: readonly RateCardEntry[];
}

/**
 * Is an entry active? The `active` flag, absent meaning active.
 *
 * @param e The entry to test.
 * @returns True unless `active` is explicitly false.
 */
export function activeEntry(e: RateCardEntry): boolean {
  return e.active !== false;
}

// ————————————————————————————————————————————————————————————————
// The legacy form — read for migration, never written
// ————————————————————————————————————————————————————————————————

/** @deprecated Legacy degressive tier of a historical grid. */
export interface ClientRateCardTier {
  minThreshold: number;
  maxThreshold: number;
  rate: number;
}

/** @deprecated Legacy: the three separate mechanisms, merged by the migration. */
export const GRID_MODES = ["discount", "specific_discount", "dedicated_tiers", "net_price"] as const;
export type GridMode = (typeof GRID_MODES)[number];

/** Targets of the legacy admin form — a subset of `EntryTarget` without services. */
export const GRID_TARGET_TYPES = ["item", "combination"] as const;
export type GridTargetType = (typeof GRID_TARGET_TYPES)[number];

/** @deprecated Legacy form still written by the current admin screen and by saved quote states;
 *  `normalizeClientRateCards` migrates it on the fly. */
export interface ClientRateCard {
  id: string;
  mode: GridMode;
  targetType: GridTargetType;
  targetRef: string;
  name?: string;
  rate?: number;
  tiers?: ClientRateCardTier[];
  price?: number;
  active?: boolean;
}

/**
 * Pure migration of the legacy model: per target, specific discount and dedicated tiers merge into
 * one entry, and a net price becomes a € base with a flat scale. A net price on an item overrode
 * the whole line's PV, a scope preserved by `linePriceScope`.
 *
 * @param legacyCards The legacy cards, any mode.
 * @returns The dedicated grid plus the warnings raised by the merge.
 */
export function migrateLegacyRateCards(legacyCards: readonly ClientRateCard[]): {
  grid: DedicatedClientRateCard;
  warnings: string[];
} {
  const warnings: string[] = [];
  const byTarget = new Map<string, ClientRateCard[]>();
  for (const g of legacyCards) {
    if (g.active === false) continue;
    const key = g.targetType + "|" + g.targetRef;
    (byTarget.get(key) ?? byTarget.set(key, []).get(key)!).push(g);
  }
  const entries: RateCardEntry[] = [];
  for (const [, cards] of byTarget) {
    // Legacy `discount` is the same shape as `specific_discount` (a % rate on the target).
    const specific = cards.find((g) => g.mode === "specific_discount" || g.mode === "discount");
    const dedicatedTiers = cards.find((g) => g.mode === "dedicated_tiers");
    const netPrice = cards.find((g) => g.mode === "net_price");
    // The net price wins (legacy resolution): when present it forms the entry on its own.
    if (netPrice) {
      const price = num(netPrice.price);
      const e: RateCardEntry = {
        id: netPrice.id,
        targetType: netPrice.targetType,
        refs: [netPrice.targetRef],
        basePrice: price,
        scale: [{ minThreshold: 0, price }],
        ...(netPrice.name ? { name: netPrice.name } : {}),
        ...(netPrice.targetType === "item" ? { linePriceScope: true } : {}),
      };
      if (netPrice.targetType === "item") {
        warnings.push(engineMessage("engineRateCardNetPriceItemMigrated", { target: netPrice.targetRef }));
      }
      entries.push(e);
      if (specific || dedicatedTiers) {
        warnings.push(engineMessage("engineRateCardNetPriceWins", { target: netPrice.targetRef }));
      }
      continue;
    }
    if (!specific && !dedicatedTiers) continue;
    const carrier = specific ?? dedicatedTiers!;
    const e: RateCardEntry = {
      id: carrier.id,
      targetType: carrier.targetType,
      refs: [carrier.targetRef],
      ...(carrier.name ?? dedicatedTiers?.name ? { name: (carrier.name ?? dedicatedTiers?.name)! } : {}),
      // Base comes from the specific discount; without it the base stays inherited from the agency one.
      ...(specific ? { base: num(specific.rate) } : {}),
      ...(dedicatedTiers
        ? {
            scale: dedicatedTiers.tiers?.length
              ? dedicatedTiers.tiers.map((p) => ({ minThreshold: p.minThreshold, rate: p.rate }))
              : [{ minThreshold: 0, rate: num(dedicatedTiers.rate) }],
          }
        : {}),
    };
    entries.push(e);
    // A duplicate of the same mode on the same target never merges silently: each extra becomes its
    // own entry and `validateEntries` blocks at load time, matching the legacy behaviour.
    for (const g of cards) {
      if (g === specific || g === dedicatedTiers || g.mode === "net_price") continue;
      entries.push({ id: g.id, targetType: g.targetType, refs: [g.targetRef], ...(g.rate != null ? { base: num(g.rate) } : {}) });
      warnings.push(engineMessage("engineRateCardDuplicateGrid", { target: g.targetRef, mode: g.mode, id: g.id }));
    }
  }
  return { grid: { entries }, warnings: warnings };
}

/**
 * Normalises engine input: accepts the dedicated grid, the legacy array (migrated on the fly), or nothing.
 *
 * @param g The dedicated grid, the legacy cards, or undefined.
 * @returns The dedicated grid (empty when nothing is given).
 */
export function normalizeClientRateCards(
  g: DedicatedClientRateCard | readonly ClientRateCard[] | undefined,
): DedicatedClientRateCard {
  if (!g) return { entries: [] };
  if (Array.isArray(g)) return migrateLegacyRateCards(g as readonly ClientRateCard[]).grid;
  return g as DedicatedClientRateCard;
}

/**
 * One active entry per target: overlapping active entries are an explicit load error.
 *
 * @param grid The dedicated grid to check.
 * @returns The error message, or null when valid.
 */
export function validateEntries(grid: DedicatedClientRateCard): string | null {
  const seen = new Map<string, string>();
  for (const e of grid.entries) {
    if (!activeEntry(e)) continue;
    for (const ref of e.refs) {
      const key = e.targetType + "|" + ref;
      const previousId = seen.get(key);
      if (previousId && previousId !== e.id) {
        return engineMessage("engineRateCardOverlappingEntries", { target: ref, first: previousId, second: e.id });
      }
      seen.set(key, e.id);
    }
  }
  return null;
}

// ————————————————————————————————————————————————————————————————
// Thresholds and tiers
// ————————————————————————————————————————————————————————————————

/**
 * Thresholds of the general grid (injected margin_tiers).
 *
 * @param generalGrid The general volume grid.
 * @returns The tiers' lower bounds, in grid order.
 */
export function generalThresholds(generalGrid: VolumeGrid): number[] {
  return generalGrid.tiers.map((p) => p.minThreshold);
}

/**
 * Thresholds of an entry: its own scale if present, else the client grid's set, else the standard
 * ones. Sorted, with the first tier starting at 0 so any cumul has a tier.
 *
 * @param e The entry.
 * @param grid The client's dedicated grid (its threshold set applies when the entry has no scale).
 * @param generalGrid The general volume grid (standard thresholds).
 * @returns Ascending thresholds, starting at 0.
 */
export function entryThresholds(
  e: RateCardEntry,
  grid: DedicatedClientRateCard,
  generalGrid: VolumeGrid,
): number[] {
  const rawThresholds = e.scale?.length
    ? e.scale.map((t) => t.minThreshold)
    : grid.thresholds?.length
      ? [...grid.thresholds]
      : generalThresholds(generalGrid);
  const sortedThresholds = [...rawThresholds].sort((a, b) => a - b);
  return sortedThresholds[0] === 0 ? sortedThresholds : [0, ...sortedThresholds];
}

/**
 * Tier index of a cumul: the highest threshold ≤ cumul (lower bound inclusive).
 *
 * @param thresholds Ascending thresholds.
 * @param cumul The cumulated quantity (m² or linear metres).
 * @returns The index of the matching tier.
 */
export function tierIndex(thresholds: readonly number[], cumul: number): number {
  let idx = 0;
  for (let i = 0; i < thresholds.length; i++) if (cumul >= thresholds[i]!) idx = i;
  return idx;
}

/**
 * Effective base of an entry: the implicit % of a € base (1 − price / target's full price), else
 * the % base (an explicit 0 counts as set), else the inherited agency discount.
 *
 * @param e The entry.
 * @param agencyDiscount The quote's agency discount (fraction), inherited when the entry sets no base.
 * @param fullTarget Full catalogue price of the target, for the € to % conversion.
 * @returns The effective base (fraction).
 */
export function effectiveBase(e: RateCardEntry, agencyDiscount: number, fullTarget: number): number {
  if (e.basePrice != null) return fullTarget > 0 ? 1 - num(e.basePrice) / fullTarget : 0;
  if (e.base != null) return num(e.base);
  return agencyDiscount;
}

/**
 * Composes base with tier rate, capped at 60%.
 *
 * @param base The effective base (fraction).
 * @param tierRate The tier's scale rate (fraction).
 * @returns The composed rate (fraction, at most 0.6).
 */
export function composeRate(base: number, tierRate: number): number {
  return Math.min(0.6, 1 - (1 - base) * (1 - tierRate));
}

/** Resolved tier of an entry for a given cumul: the effective rate to apply, plus the imposed € price. */
export interface ResolvedTier {
  /** Effective rate against the target's full price (may be negative when a € price marks up). */
  rate: number;
  /** Lower bound of the selected tier. */
  minThreshold: number;
  index: number;
  /** True when the selected tier is priced in € (price as-is). */
  inEuros: boolean;
  /** The tier's € price when it carries one. */
  price?: number;
  /** The composed effective base (agency discount column). */
  base: number;
  /** The tier's scale rate (tier discount column). */
  scaleRate: number;
}

/**
 * Resolves an entry's tier: entry thresholds, the standard cumul mechanics, then composition.
 * A tier with no value, or a missing scale, falls back to the general grid's standard rate at
 * the tier's lower bound.
 *
 * @param e The entry.
 * @param cumul The cumulated quantity (m² or linear metres).
 * @param ctx The client grid, general grid, agency discount and full target price.
 * @returns The resolved tier.
 */
export function resolveEntryTier(
  e: RateCardEntry,
  cumul: number,
  ctx: {
    grid: DedicatedClientRateCard;
    generalGrid: VolumeGrid;
    /** The quote's agency discount (inherited base of a migrated entry without a specific discount). */
    agencyDiscount: number;
    /** Full catalogue price of the target, used for the € to % conversion. */
    fullTarget: number;
  },
): ResolvedTier {
  const thresholds = entryThresholds(e, ctx.grid, ctx.generalGrid);
  const idx = tierIndex(thresholds, cumul);
  const minThreshold = thresholds[idx]!;
  const tier = e.scale?.find((t) => t.minThreshold === minThreshold);
  const base = effectiveBase(e, ctx.agencyDiscount, ctx.fullTarget);
  if (tier && tier.price != null) {
    const price = num(tier.price);
    const rate = ctx.fullTarget > 0 ? 1 - price / ctx.fullTarget : 0;
    return { rate, minThreshold, index: idx, inEuros: true, price, base, scaleRate: rate };
  }
  const scaleRate =
    tier && tier.rate != null
      ? num(tier.rate)
      : marginTier(minThreshold, ctx.generalGrid).discountRate; // default: the standard rate at the lower bound
  return { rate: composeRate(base, scaleRate), minThreshold, index: idx, inEuros: false, base, scaleRate };
}

// ————————————————————————————————————————————————————————————————
// Per-line resolution: combination > dedicated item > material propagation > standard
// ————————————————————————————————————————————————————————————————

/** Components of a print line, for per-component resolution. */
export interface LineComponents {
  material?: string | null | undefined;
  print?: string | null | undefined;
  cut?: string | null | undefined;
  lamination?: string | null | undefined;
  finishings?: readonly (string | null | undefined)[] | undefined;
}

/** Entries applicable to a line. A `combination` entry locks everything; otherwise each component
 *  carries its own, and the material propagates to the print only. */
export interface LineEntries {
  combination?: RateCardEntry;
  material?: RateCardEntry;
  print?: RateCardEntry;
  cut?: RateCardEntry;
  lamination?: RateCardEntry;
  finishings: (RateCardEntry | undefined)[];
}

/**
 * The active entry targeting a ref (undefined when none); uniqueness is guaranteed elsewhere.
 *
 * @param grid The dedicated grid.
 * @param targetType The kind of target.
 * @param ref The catalogue ref, combination ref or service code.
 * @returns The entry, or undefined.
 */
export function resolveEntryRef(
  grid: DedicatedClientRateCard,
  targetType: EntryTarget,
  ref: string | null | undefined,
): RateCardEntry | undefined {
  if (!ref) return undefined;
  return grid.entries.find((e) => activeEntry(e) && e.targetType === targetType && e.refs.includes(ref));
}

/**
 * Entries applicable to a print line. `null` means no entry concerns the line, so the standard
 * path applies. `combiRefs` lists the combination refs the line satisfies.
 *
 * @param grid The dedicated grid.
 * @param l The line's components.
 * @param combiRefs The combination refs the line satisfies.
 * @returns The applicable entries, or null.
 */
export function resolveLineEntries(
  grid: DedicatedClientRateCard,
  l: LineComponents,
  combiRefs: readonly string[],
): LineEntries | null {
  if (!grid.entries.length) return null;
  const combination = grid.entries.find(
    (e) => activeEntry(e) && e.targetType === "combination" && e.refs.some((r) => combiRefs.includes(r)),
  );
  if (combination) return { combination, finishings: [] };
  const material = resolveEntryRef(grid, "item", l.material);
  const print = resolveEntryRef(grid, "item", l.print);
  const cut = resolveEntryRef(grid, "item", l.cut);
  const lamination = resolveEntryRef(grid, "item", l.lamination);
  const finishings = (l.finishings ?? []).map((c) => resolveEntryRef(grid, "item", c));
  if (!material && !print && !cut && !lamination && !finishings.some(Boolean)) return null;
  return {
    ...(material ? { material } : {}),
    ...(print ? { print } : {}),
    ...(cut ? { cut: cut } : {}),
    ...(lamination ? { lamination: lamination } : {}),
    finishings: finishings,
  };
}

// ————————————————————————————————————————————————————————————————
// Per-tier margins — lower-bound convention
// ————————————————————————————————————————————————————————————————

/** Margin of a client tier, with PA resolved at its lower bound. */
export interface TierMargin {
  minThreshold: number;
  /** Client PV of the tier (same unit as the full price); null when the full price is missing. */
  pvClient: number | null;
  /** PA at the lower bound (workshop grid when supplied, else target PA). */
  pa: number | null;
  /** Tier margin = 1 − PA / client PV; this is the minimum margin, which can only improve with volume. */
  marginPct: number | null;
  paOrigin: "grid" | "target" | null;
}

/**
 * Per-tier margins of an entry (pure, lower-bound convention). A tier's PA resolves at its lower
 * bound against the general tiers: the real PA when the workshop grid is supplied, else the target
 * PA formula. This is the tier's minimum margin, which the warning and the floor gate judge.
 *
 * @param e The entry.
 * @param ctx The client grid, general grid, agency discount, full target price and optional workshop PA grid.
 * @returns One margin per tier of the entry.
 */
export function marginsByTier(
  e: RateCardEntry,
  ctx: {
    grid: DedicatedClientRateCard;
    generalGrid: VolumeGrid;
    agencyDiscount: number;
    /** Full catalogue price of the target. */
    fullTarget: number;
    /** Workshop PA grid of the target: 6 values at the general tiers (€/m²). Absent means target PA. */
    pa6?: readonly number[];
  },
): TierMargin[] {
  const thresholds = entryThresholds(e, ctx.grid, ctx.generalGrid);
  const gen = generalThresholds(ctx.generalGrid);
  return thresholds.map((minThreshold) => {
    const resolved = resolveEntryTier(e, minThreshold, ctx);
    const pvClient = ctx.fullTarget > 0 ? round2(resolved.inEuros ? resolved.price! : ctx.fullTarget * (1 - resolved.rate)) : null;
    const generalTier = marginTier(minThreshold, ctx.generalGrid);
    let pa: number | null;
    let paOrigin: TierMargin["paOrigin"];
    if (ctx.pa6 && ctx.pa6.length) {
      const generalIndex = Math.min(tierIndex(gen, minThreshold), ctx.pa6.length - 1);
      pa = round2(num(ctx.pa6[generalIndex]));
      paOrigin = "grid";
    } else if (ctx.fullTarget > 0) {
      pa = round2(ctx.fullTarget * (1 - generalTier.discountRate) * (1 - generalTier.targetMargin)); // target PA formula
      paOrigin = "target";
    } else {
      pa = null;
      paOrigin = null;
    }
    const marginPct =
      pvClient != null && pvClient > 0 && pa != null ? Math.round((1 - pa / pvClient) * 1000) / 10 : null;
    return { minThreshold, pvClient, pa, marginPct: marginPct, paOrigin };
  });
}

/**
 * Lower bounds of the tiers whose minimum margin falls below the floor.
 *
 * @param margins Per-tier margins.
 * @param floorPct The floor, in percent.
 * @returns The lower bounds of the tiers below the floor.
 */
export function tiersBelowFloor(margins: readonly TierMargin[], floorPct: number): number[] {
  return margins.filter((m) => m.marginPct != null && m.marginPct < floorPct).map((m) => m.minThreshold);
}

// ————————————————————————————————————————————————————————————————
// Services — base only
// ————————————————————————————————————————————————————————————————

/**
 * Client price of a service served by an entry: the € base as-is, else grid price × (1 − base %).
 * No scale, no minimums, no public-rate flag; an absent base means 0 since the agency discount
 * never applies to services.
 *
 * @param e The entry serving the service.
 * @param gridPrice The service's grid price.
 * @returns The client price.
 */
export function serviceEntryPrice(e: RateCardEntry, gridPrice: number): number {
  if (e.basePrice != null) return num(e.basePrice);
  const base = e.base != null ? num(e.base) : 0;
  return round2(gridPrice * (1 - Math.min(0.6, Math.max(0, base))));
}
